/* eslint-disable no-console */
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@DSG/MobiusOverrides/Breadcrumbs/Breadcrumbs";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getHomePageUrl,
    getPageLinkByNodeId,
    getPageLinkByPageType,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { LinkProps } from "@insite/mobius/Link";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        isProductDetailsPage: getCurrentPage(state).type === "ProductDetailsPage",
        isDealerDetailsPage: getCurrentPage(state).type === "DealerDetailsPage",
        locationFinderDetails: getPageLinkByPageType(state, "LocationFinderPage"),
        isSavedOrderDetailsPage:getCurrentPage(state).type === "SavedOrderDetailsPage",
        page:getCurrentPage(state),
        isOrderHistoryDetailsPage:getCurrentPage(state).type === "OrderDetailsPage",
        isInvoiceDetailsPage: getCurrentPage(state).type === "InvoiceDetailsPage"
      
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            padding: 32px 0;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;


const PageBreadcrumbs = (props: Props) => {
    let links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);

    if (links.length > 0 && links.some(o => o.type === "HomePage" || o.type === undefined)) {
        // we need to always direct to the language-specific homepage URL
        const homePageLink = { children: translate("Home_Breadcrumb"), href: props.homePageUrl };
        const linksWithoutHomePageLink = [...links].splice(1, links.length);
        links = [homePageLink, ...linksWithoutHomePageLink];
    }

    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);

    links = links.filter(x => x.children !== "Home" && x.href !== null);
    if (props.isDealerDetailsPage) {
        const newLinksArray = [
            { children: "Locations", href: props.locationFinderDetails?.url, type: props.locationFinderDetails?.type },
            ...links,
        ];

        return (
            <Breadcrumbs
                links={newLinksArray}
                isProductDetailsPage={props.isProductDetailsPage}
                data-test-selector="pageBreadcrumbs"
                {...styles.breadcrumbs}
            />
        );
    }
    if (typeof window !== "undefined" && props.isInvoiceDetailsPage) {
        // Get the query string from the URL
        const queryString = window.location.search;

        // Parse the query string
        // const parsedParams = parseQueryString<>(queryString);
        const parsedQuery = parseQueryString<{ invoiceNumber?: string }>(queryString);
        const invoiceDetailLink = links.filter(x => x.children === "Invoice Details");
        if (invoiceDetailLink && invoiceDetailLink[0]) {
            invoiceDetailLink[0].children = translate("Invoice#: ") + parsedQuery.invoiceNumber;
        }

        return (
            <Breadcrumbs
                links={links}
                isProductDetailsPage={props.isProductDetailsPage}
                data-test-selector="pageBreadcrumbs"
                {...styles.breadcrumbs}
            />
        );
    }

  
    if(props.isOrderHistoryDetailsPage)
    {
        const orderDetailsLink = links.filter(x=>x.children == "Order Details");
        if(orderDetailsLink && orderDetailsLink[0])
        {
            orderDetailsLink[0].children = translate("Order Detail");
        }
                    
        return <Breadcrumbs links={links} isProductDetailsPage={props.isProductDetailsPage} data-test-selector="pageBreadcrumbs" {...styles.breadcrumbs} />; 
    }
    
      
   if(props.isSavedOrderDetailsPage)
    {
       
        const savedOrderBreadcrumbLinks = links.map(obj => {
            if (obj.children === "Saved Orders") {
                return { ...obj, children: translate("Saved Carts") };
            }
            if (obj.children === "Saved Order Details") {
                return { ...obj, children: translate("Saved Cart") };
            }
            return obj;
        });

        if (savedOrderBreadcrumbLinks) {
            links = savedOrderBreadcrumbLinks;
        }
    }

    if (links.length > 1) {
        return (
            <Breadcrumbs
                links={links}
                isProductDetailsPage={props.isProductDetailsPage}
                data-test-selector="pageBreadcrumbs"
                {...styles.breadcrumbs}
            />
        );
    }

    return <Breadcrumbs />;
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);

    const vmiPageTypes: string[] = [
        "VmiDashboardPage",
        "VmiLocationsPage",
        "VmiLocationDetailsPage",
        "VmiBinsPage",
        "VmiBinDetailsPage",
        "VmiReportingPage",
        "VmiUsersPage",
        "VmiOrderHistoryPage",
        "VmiOrderDetailsPage",
    ];
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : vmiPageTypes.some(search(currentLink.type));

    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({ children: currentLink.title, href: currentLink.url, type: currentLink.type });
        }

        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
